<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import AppImage from '@uiKit/elements/Image.vue'
import AuthShowcaseSlide from './AuthShowcaseSlide.vue'
import { AUTH_SHOWCASE_INTERVAL_MS, AUTH_SHOWCASE_SLIDES } from '../constants/authShowcaseSlides'

// Photo column beside every pre-auth screen: five "why sell with Sliceal"
// slides that crossfade over Dubai towers, with the story anchored bottom-left
// and the slide dots pinned beneath it. Mounted by SignUpLayout, which hides it
// below 992px. Marketing chrome only — no data, no side effects beyond its own
// timer.
//
// The rotation is opt-out by context, not by setting: it holds while the
// pointer or keyboard focus is inside the panel (someone is reading or picking
// a dot), skips ticks while the tab is hidden, and never starts for a user who
// asked the OS for reduced motion. Picking a dot restarts the interval so the
// chosen slide gets its full time on screen.
const slides = AUTH_SHOWCASE_SLIDES
const activeIndex = ref(0)
const isPaused = ref(false)
let rotationTimer: ReturnType<typeof setInterval> | null = null

const activeSlide = computed(() => slides[activeIndex.value])

function advance(): void {
  if (isPaused.value || document.hidden) return
  activeIndex.value = (activeIndex.value + 1) % slides.length
}

function stopRotation(): void {
  if (rotationTimer) clearInterval(rotationTimer)
  rotationTimer = null
}

function startRotation(): void {
  stopRotation()
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  rotationTimer = setInterval(advance, AUTH_SHOWCASE_INTERVAL_MS)
}

function showSlide(index: number): void {
  activeIndex.value = index
  startRotation()
}

onMounted(startRotation)
onUnmounted(stopRotation)
</script>

<template>
  <Box
    is="section"
    class="auth-showcase"
    aria-roledescription="carousel"
    aria-label="Why sell with Sliceal"
    block
    @mouseenter="isPaused = true"
    @mouseleave="isPaused = false"
    @focusin="isPaused = true"
    @focusout="isPaused = false"
  >
    <!-- Every photo stays mounted so the crossfade has both frames to blend and
         the next slide is already decoded when its turn comes. -->
    <Box
      v-for="(slide, index) in slides"
      :key="slide.id"
      :class="['auth-showcase__frame', { 'auth-showcase__frame--active': index === activeIndex }]"
      aria-hidden="true"
    >
      <AppImage :src="slide.photo" alt="" class="auth-showcase__photo" />
      <Box class="auth-showcase__wash" :style="{ '--auth-showcase-wash': slide.washOpacity }" />
    </Box>

    <Box direction="col" gap="2.75rem" class="auth-showcase__content">
      <Transition name="auth-showcase-story" mode="out-in">
        <AuthShowcaseSlide
          v-if="activeSlide"
          :key="activeSlide.id"
          :slide="activeSlide"
          role="group"
          aria-roledescription="slide"
          :aria-label="`${activeIndex + 1} of ${slides.length}`"
        />
      </Transition>

      <Box flex align="center" gap="1">
        <Button
          v-for="(slide, index) in slides"
          :key="slide.id"
          variant="text"
          :aria-label="`Show slide ${index + 1}: ${slide.title}`"
          :aria-current="index === activeIndex ? 'true' : undefined"
          class="auth-showcase__dot-hit"
          @click="showSlide(index)"
        >
          <Box
            :class="['auth-showcase__dot', { 'auth-showcase__dot--active': index === activeIndex }]"
          />
        </Button>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.auth-showcase {
  position: relative;
  height: 100%;
  overflow: hidden;
  border-left: 1px solid var(--p-content-border-color);
}

.auth-showcase__frame {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.8s ease;
}

.auth-showcase__frame--active {
  opacity: 1;
}

.auth-showcase__photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.auth-showcase__wash {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, var(--auth-showcase-wash)) 0%,
    rgba(54, 54, 54, var(--auth-showcase-wash)) 100%
  );
}

/* Story + dots, bottom-left: inset ~60px from the left edge and lifted clear of
   the bottom so the dots hold the same spot on every slide. Capped so the copy
   keeps a readable measure on wide screens. */
.auth-showcase__content {
  position: absolute;
  left: 3.75rem;
  right: 3.75rem;
  bottom: 7rem;
  max-width: 613px;
}

/* The pill is 10px tall; the button around it stretches the hit area to 30px
   without pushing the pills apart. */
.auth-showcase__dot-hit {
  padding: 0.625rem 0 !important;
}

.auth-showcase__dot {
  width: 20px;
  height: 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.35);
  transition:
    width 0.3s ease,
    background-color 0.3s ease;
}

.auth-showcase__dot--active {
  width: 34px;
  background: var(--p-primary-contrast-color);
}

.auth-showcase-story-enter-active,
.auth-showcase-story-leave-active {
  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
}

.auth-showcase-story-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.auth-showcase-story-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .auth-showcase__frame,
  .auth-showcase__dot,
  .auth-showcase-story-enter-active,
  .auth-showcase-story-leave-active {
    transition: none;
  }
}
</style>
